/**
 * Analytics PDF builder — matches the GoodData reference layout.
 *
 * Sample references: `dev-samples/mvp/GoodData Sample Reports/*.pdf`
 *
 * Visual contract:
 *   - Portrait A4
 *   - White background, no Neeve branding chrome
 *   - Header: dashboard title (top-left) + date (top-right), 9pt
 *   - Body: full-width image of the rendered dashboard, paginated vertically
 *     if it exceeds one page
 *   - Footer: "Page X/Y" bottom-right, 9pt
 *
 * Strategy: capture the dashboard DOM via html2canvas at 2x scale (retina-ish),
 * then slice the resulting canvas across PDF pages. This means the PDF
 * automatically picks up whatever is currently rendered on the page — same
 * filter state, same selected chart library, same widget content.
 *
 * Caller passes a ref to a single container element wrapping the dashboard's
 * widget stack (NOT the page header — the page header is rendered by this
 * util into the PDF header). See ActiveEndpoints / ActiveUsers / Overview /
 * SecurityPolicies for the usage pattern.
 */

import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';

const A4_W_MM = 210;
const A4_H_MM = 297;
const MARGIN_MM = 12;
const HEADER_H_MM = 10;
const FOOTER_H_MM = 8;

const CONTENT_W_MM = A4_W_MM - MARGIN_MM * 2;
const CONTENT_H_MM = A4_H_MM - MARGIN_MM * 2 - HEADER_H_MM - FOOTER_H_MM;

export interface AnalyticsPdfOptions {
  /** Dashboard title — top-left of every page. */
  title: string;
  /** Filename slug (no extension). */
  filenameSlug: string;
  /** DOM element containing the widget stack to capture. */
  target: HTMLElement;
}

function formatDate(d: Date): string {
  const m = d.getMonth() + 1;
  const day = d.getDate();
  const y = d.getFullYear();
  return `${m}/${day}/${y}`;
}

function drawHeaderFooter(doc: jsPDF, opts: { title: string; pageNum: number; totalPages: number; dateLabel: string }): void {
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(40, 40, 40);
  // Header — title left, date right
  doc.text(opts.title, MARGIN_MM, MARGIN_MM);
  doc.text(opts.dateLabel, A4_W_MM - MARGIN_MM, MARGIN_MM, { align: 'right' });
  // Footer — page X/Y right
  doc.setTextColor(110, 110, 110);
  doc.text(`Page ${opts.pageNum}/${opts.totalPages}`, A4_W_MM - MARGIN_MM, A4_H_MM - MARGIN_MM + 4, { align: 'right' });
}

/**
 * Collect Y-positions in the captured canvas where it's safe to split a page
 * without cutting a card in half.
 *
 * Algorithm: each `[data-pdf-block="card"]` contributes a Y-range
 * [top, bottom]. Cards in the same flex/grid row overlap (same Y span), so
 * we MERGE overlapping ranges into one "block". Safe break points are only
 * at the boundaries of merged blocks (gaps between rows of cards) — never
 * inside a block.
 *
 * Why merge: a previous version added each card's top + bottom independently
 * to the safe-break set. For cards in the same row with slightly different
 * heights (e.g., one card has multi-line title), the algorithm would pick the
 * shorter card's bottom as a break, leaving the taller card's bottom edge to
 * overflow onto the next page.
 */
function collectSafeBreakPoints(target: HTMLElement, canvasHeightPx: number, scaleY: number): number[] {
  const targetRect = target.getBoundingClientRect();
  const cards = target.querySelectorAll<HTMLElement>('[data-pdf-block="card"]');
  const ranges: Array<[number, number]> = [];
  cards.forEach((c) => {
    const r = c.getBoundingClientRect();
    const top = Math.max(0, (r.top - targetRect.top) * scaleY);
    const bottom = Math.min(canvasHeightPx, (r.bottom - targetRect.top) * scaleY);
    if (bottom > top) ranges.push([top, bottom]);
  });
  ranges.sort((a, b) => a[0] - b[0]);

  // Merge overlapping/adjacent ranges. Cards in the same flex/grid row will
  // overlap in Y; this collapses them into a single un-breakable block.
  const merged: Array<[number, number]> = [];
  for (const r of ranges) {
    const last = merged[merged.length - 1];
    if (last && r[0] <= last[1]) {
      last[1] = Math.max(last[1], r[1]);
    } else {
      merged.push([r[0], r[1]]);
    }
  }

  const points = new Set<number>([0, canvasHeightPx]);
  for (const [start, end] of merged) {
    points.add(start);
    points.add(end);
  }
  return Array.from(points).sort((a, b) => a - b);
}

/**
 * Pick the slice end-Y for a page that starts at `startY`. Prefers the
 * largest safe break ≤ startY + pageHeightPx. Falls back to a hard cut at
 * startY + pageHeightPx if no safe break fits — that case is unavoidable
 * for cards that are taller than a single page (e.g., long service lists).
 */
function pickEndY(safeBreaks: number[], startY: number, pageHeightPx: number, totalHeightPx: number): number {
  const candidate = Math.min(startY + pageHeightPx, totalHeightPx);
  if (candidate >= totalHeightPx) return totalHeightPx;
  // Find the largest safe break in (startY, candidate].
  let best = -1;
  for (const p of safeBreaks) {
    if (p > startY && p <= candidate) {
      if (p > best) best = p;
    } else if (p > candidate) {
      break;
    }
  }
  if (best > startY) return best;
  // No safe break fits — single block exceeds one page. Hard-cut at candidate
  // so we don't loop forever. The block gets visually split; an info tooltip
  // on Service Health calls this out so it's a known constraint.
  return candidate;
}

/**
 * Export the given dashboard DOM as a GoodData-style PDF.
 *
 * Returns when the file has been triggered for download.
 */
export async function exportAnalyticsPdf(opts: AnalyticsPdfOptions): Promise<void> {
  const { title, filenameSlug, target } = opts;

  // Capture the target element at 2x scale. backgroundColor: '#ffffff' forces
  // a white canvas (matches GoodData) regardless of theme tokens.
  const canvas = await html2canvas(target, {
    scale: 2,
    backgroundColor: '#ffffff',
    useCORS: true,
    logging: false,
    // Skip elements explicitly marked (e.g., the chart-lib corner badge,
    // export button itself) by setting data-pdf-exclude="true" on them.
    ignoreElements: (el) => (el as HTMLElement).dataset?.pdfExclude === 'true',
  });

  const targetCssHeight = target.getBoundingClientRect().height;
  const scaleY = canvas.height / targetCssHeight; // canvas px per DOM px
  // canvas px per page-mm at the CONTENT_W_MM rendering scale:
  const canvasPxPerMm = canvas.width / CONTENT_W_MM;
  const pageHeightPx = CONTENT_H_MM * canvasPxPerMm;

  // First pass: walk page boundaries to compute total page count.
  const safeBreaks = collectSafeBreakPoints(target, canvas.height, scaleY);
  const pageBreaks: number[] = [0];
  let cursor = 0;
  while (cursor < canvas.height) {
    const next = pickEndY(safeBreaks, cursor, pageHeightPx, canvas.height);
    if (next <= cursor) break; // defensive — shouldn't happen but guards infinite loops
    pageBreaks.push(next);
    cursor = next;
  }
  const totalPages = Math.max(1, pageBreaks.length - 1);

  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const dateLabel = formatDate(new Date());

  // Scratch canvas for per-page slicing.
  const sliceCanvas = document.createElement('canvas');
  const sliceCtx = sliceCanvas.getContext('2d');
  if (!sliceCtx) throw new Error('Failed to get 2D context for PDF slicing');

  for (let pageNum = 1; pageNum <= totalPages; pageNum++) {
    if (pageNum > 1) doc.addPage();
    drawHeaderFooter(doc, { title, pageNum, totalPages, dateLabel });

    const sourceY = pageBreaks[pageNum - 1];
    const sourceEnd = pageBreaks[pageNum];
    const sourceH = sourceEnd - sourceY;
    if (sourceH <= 0) continue;

    sliceCanvas.width = canvas.width;
    sliceCanvas.height = sourceH;
    sliceCtx.fillStyle = '#ffffff';
    sliceCtx.fillRect(0, 0, sliceCanvas.width, sliceCanvas.height);
    sliceCtx.drawImage(canvas, 0, sourceY, canvas.width, sourceH, 0, 0, canvas.width, sourceH);

    const imgData = sliceCanvas.toDataURL('image/png');
    const imgHeightMm = sourceH / canvasPxPerMm;
    doc.addImage(
      imgData,
      'PNG',
      MARGIN_MM,
      MARGIN_MM + HEADER_H_MM,
      CONTENT_W_MM,
      imgHeightMm,
      undefined,
      'FAST'
    );
  }

  // Filename: <slug>-YYYY-MM-DD.pdf (e.g. security-policies-2026-05-20.pdf)
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  doc.save(`${filenameSlug}-${yyyy}-${mm}-${dd}.pdf`);
}
